import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import {
  useMountErrorMessage,
  useOpenOnDesktopAction,
} from '@frameio/shared/resources/mounted-storage';
import { MenuButton, MenuGroup, Tooltip } from '@frameio/vapor';
import {
  IconDownload,
  IconFolderArrowRight,
  IconLink,
  IconOpenIn,
} from '@frameio/vapor-icons';
import {
  useSearchHistoryData,
  useTrackActionedOnResults,
} from '../../../shared/contexts';
import { useAssetActions } from '../../../shared/hooks/useAssetActions';
import type { SearchMode } from '../../../shared/types';
import { getResultSourceFromSectionId } from '../../../shared/utils';
import { useQuickSearchAppliedFilterTypes } from '../../hooks';
import TEXT from './generated/TEXT/AssetContextMenuContent';

TEXT.DEFINE({
  /** Menu item label to copy asset URL */
  copyUrl: 'Copy URL',
  /** Menu item label to view asset in its project */
  viewInProject: 'View in Project',
  /** Menu item label to view asset in trash/recently deleted */
  viewInTrash: 'Go to Recently Deleted',
  /** Menu item label to download asset */
  download: 'Download',
  /** Menu item label to import asset (used in Premiere client) */
  import: 'Import',
  /** Menu item label to open asset in a new tab */
  openInNewTab: 'Open in New Tab',
});

type AssetContextMenuContentProps = {
  assetId: string;
  itemPosition: number;
  searchMode: SearchMode;
  searchQuery: string;
};

export const AssetContextMenuContent = ({
  assetId,
  itemPosition,
  searchMode,
  searchQuery,
}: AssetContextMenuContentProps) => {
  const isPremiereClient = useIsMatchingClientName('premiere');
  const resultSource = getResultSourceFromSectionId(searchMode);
  const trackActionedOnResults = useTrackActionedOnResults();
  const appliedFilterTypes = useQuickSearchAppliedFilterTypes();
  const { addRecentSearch } = useSearchHistoryData();
  const {
    canDownloadAsset,
    projectId,
    handleCopyLink,
    handleDownload,
    handleViewInProject,
    handleViewInTrash,
    isRecentlyDeleted,
    handleOpenInNewTab,
    resultType,
    matchType,
  } = useAssetActions({
    appliedFilterTypes,
    assetId,
    resultPosition: itemPosition,
    resultSource,
    searchMode,
    searchQuery,
  });

  const {
    icon: openOnDesktopIcon,
    isHidden: isOpenOnDesktopHidden,
    isDisabled: isOpenOnDesktopDisabled,
    label: openOnDesktopLabel,
    onPress: handleOpenOnDesktop,
    error: openOnDesktopError,
  } = useOpenOnDesktopAction({
    projectId,
    assetId,
    canOpenOnDesktop: canDownloadAsset,
    trackingSource: 'ContextMenu',
    onPress: () => {
      addRecentSearch({ query: searchQuery, mode: searchMode });
      trackActionedOnResults({
        action: 'OpenOnDesktop',
        assetCount: resultType === 'Folder' ? 0 : 1,
        filterApplied: appliedFilterTypes,
        folderCount: resultType === 'Folder' ? 1 : 0,
        isBulkOperation: false,
        matchType,
        resultPosition: itemPosition,
        resultSource,
        resultType,
      });
    },
  });
  const openOnDesktopErrorMessage = useMountErrorMessage(openOnDesktopError);

  /**
   * If the asset is recently deleted, show the deleted asset menu subset.
   */
  if (isRecentlyDeleted) {
    return (
      <MenuButton
        iconBefore={<IconFolderArrowRight />}
        onPress={handleViewInTrash}
      >
        {TEXT.viewInTrash}
      </MenuButton>
    );
  }

  return (
    <>
      <MenuGroup>
        <MenuButton
          iconBefore={<IconFolderArrowRight />}
          onPress={handleViewInProject}
        >
          {TEXT.viewInProject}
        </MenuButton>
        <MenuButton iconBefore={<IconOpenIn />} onPress={handleOpenInNewTab}>
          {TEXT.openInNewTab}
        </MenuButton>
        {!isOpenOnDesktopHidden && (
          <Tooltip
            disabled={!isOpenOnDesktopDisabled}
            placement="left"
            title={openOnDesktopErrorMessage}
          >
            <span>
              <MenuButton
                disabled={isOpenOnDesktopDisabled}
                iconBefore={openOnDesktopIcon}
                onPress={handleOpenOnDesktop}
              >
                {openOnDesktopLabel}
              </MenuButton>
            </span>
          </Tooltip>
        )}
      </MenuGroup>
      <MenuGroup>
        {canDownloadAsset && (
          <MenuButton iconBefore={<IconDownload />} onPress={handleDownload}>
            {isPremiereClient ? TEXT.import : TEXT.download}
          </MenuButton>
        )}
        <MenuButton iconBefore={<IconLink />} onPress={handleCopyLink}>
          {TEXT.copyUrl}
        </MenuButton>
      </MenuGroup>
    </>
  );
};
